/* START_MODULE_CONTRACT
 * PURPOSE: Единое поле выбора даты на базе MUI X DatePicker (вместо native type=date).
 * SCOPE: парсинг ISO YYYY-MM-DD, формат DD.MM.YYYY, интеграция с appTheme.
 * DEPENDS: @mui/x-date-pickers, dayjs, formFieldConfig
 * LINKS: M-FE-APP
 * ROLE: RUNTIME
 * MAP_MODE: EXPORTS
 * END_MODULE_CONTRACT
 *
 * START_MODULE_MAP
 * AppDateField - поле даты с выпадающим календарём.
 * parseIsoDate - парсинг строки YYYY-MM-DD в dayjs.
 * END_MODULE_MAP
 *
 * START_CHANGE_SUMMARY
 * LAST_CHANGE: 2026-07-13 - MUI X DatePicker вместо native input type=date.
 * END_CHANGE_SUMMARY
 */

import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import { appDataFieldHtmlInputProps } from '../utils/formFieldConfig';

dayjs.extend(customParseFormat);

const ISO_DATE = 'YYYY-MM-DD';
const DISPLAY_DATE = 'DD.MM.YYYY';

/* START_METHOD_parseIsoDate */
/* START_CONTRACT: parseIsoDate
 * PURPOSE: Преобразовать ISO-строку даты в dayjs или null.
 * INPUTS: { value: string }.
 * OUTPUTS: { dayjs.Dayjs|null }.
 * SIDE_EFFECTS: нет.
 * LINKS: M-FE-APP
 * END_CONTRACT: parseIsoDate
 */
export function parseIsoDate(value) {
  if (!value) return null;
  const parsed = dayjs(value, ISO_DATE, true);
  return parsed.isValid() ? parsed : null;
}
/* END_METHOD_parseIsoDate */

/* START_METHOD_AppDateField */
/* START_CONTRACT: AppDateField
 * PURPOSE: Поле даты с современным календарём MUI X, value/onChange в формате YYYY-MM-DD.
 * INPUTS: { label, value, onChange, disabled, error, helperText, size, sx, fullWidth, margin, required, clearable, name }.
 * OUTPUTS: { JSX.Element }.
 * SIDE_EFFECTS: нет (делегирует onChange).
 * LINKS: M-FE-APP
 * END_CONTRACT: AppDateField
 */
export default function AppDateField({
  label,
  value = '',
  onChange,
  disabled = false,
  error = false,
  helperText,
  size = 'small',
  sx,
  fullWidth = true,
  margin = 'normal',
  required = false,
  clearable = false,
  name,
}) {
  const pickerValue = parseIsoDate(value);

  /* START_METHOD_handleChange */
  /* START_CONTRACT: handleChange
   * PURPOSE: Сконвертировать выбранную дату обратно в ISO-строку для API.
   * INPUTS: { newValue: dayjs.Dayjs|null }.
   * OUTPUTS: { void }.
   * SIDE_EFFECTS: onChange.
   * LINKS: M-FE-APP
   * END_CONTRACT: handleChange
   */
  const handleChange = (newValue) => {
    if (!onChange) return;
    if (!newValue || !newValue.isValid()) {
      onChange('');
      return;
    }
    onChange(newValue.format(ISO_DATE));
  };
  /* END_METHOD_handleChange */

  const textFieldSlotProps = {
    size,
    error,
    helperText,
    fullWidth,
    margin,
    required,
  };

  return (
    <DatePicker
      label={label}
      value={pickerValue}
      onChange={handleChange}
      disabled={disabled}
      format={DISPLAY_DATE}
      views={['year', 'month', 'day']}
      openTo="day"
      showDaysOutsideCurrentMonth
      fixedWeekNumber={6}
      slotProps={{
        textField: {
          ...textFieldSlotProps,
          slotProps: {
            htmlInput: {
              ...appDataFieldHtmlInputProps,
              ...(name ? { name } : {}),
            },
          },
        },
        field: {
          clearable,
        },
        openPickerButton: {
          'aria-label': label ? `Открыть календарь: ${label}` : 'Открыть календарь',
        },
        openPickerIcon: {
          fontSize: 'small',
        },
        desktopPaper: {
          elevation: 8,
          sx: { borderRadius: 1.5, mt: 0.5 },
        },
        mobilePaper: {
          elevation: 8,
          sx: { borderRadius: 1.5 },
        },
      }}
      slots={{
        openPickerIcon: CalendarMonthOutlinedIcon,
      }}
      sx={sx}
    />
  );
}
/* END_METHOD_AppDateField */
